import React, { useMemo, useState } from 'react';
import { useAppSelector } from '../../../contexts/hooks';
import { EReportId } from '../../../types/types.dashboard';
import { LoadIndicator, TagBox, Button } from 'devextreme-react';

import './SalesDashboardPersonal.scss';
import { CardAnalytics } from '../../../components/library/card-analytics/CardAnalytics';

export const SalesDashboardPersonal: React.FC = () => {
  const dashboard = useAppSelector((state) => 
    state.dashboards.dashboards.find(d => d.Db_Id === EReportId.SalesBoardPersonal)
  );

  // Filters and view state
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [selectedContracts, setSelectedContracts] = useState<string[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState<'current' | 'previous'>('current');

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value: number): string => {
    return new Intl.NumberFormat('es-CO').format(value);
  };

  const handleRefreshClick = () => {
    // Refresh functionality
  };

  const processedData = useMemo((): any => {
    if (!dashboard?.Data || dashboard.Loading) return null;

    let rawData: any[] = dashboard.Data;
    
    // Apply filters
    if (selectedServices.length > 0) {
      rawData = rawData.filter(item => selectedServices.includes(item.Se_Nombre));
    }
    if (selectedContracts.length > 0) {
      rawData = rawData.filter(item => selectedContracts.includes(item.Contrato));
    }

    // Filter data for today
    const todayData = rawData.filter(item => item.Periodo === 'Hoy');
    const currentMonthData = rawData.filter(item => item.Periodo === 'Este Mes');
    const previousMonthData = rawData.filter(item => item.Periodo === 'Mes Pasado');

    // Process today indicator
    const todayIndicator = {
      totalOrders: todayData.length,
      subtotal: todayData.reduce((sum, item) => sum + item.POrd_SubTotal, 0),
      discount: todayData.reduce((sum, item) => sum + item.POrd_Descuento, 0),
      total: todayData.reduce((sum, item) => sum + item.POrd_ValorTotal, 0)
    };

    // Process monthly data
    const currentMonth = {
      totalOrders: currentMonthData.length,
      subtotal: currentMonthData.reduce((sum, item) => sum + item.POrd_SubTotal, 0),
      discount: currentMonthData.reduce((sum, item) => sum + item.POrd_Descuento, 0),
      total: currentMonthData.reduce((sum, item) => sum + item.POrd_ValorTotal, 0)
    };

    const previousMonth = {
      totalOrders: previousMonthData.length,
      subtotal: previousMonthData.reduce((sum, item) => sum + item.POrd_SubTotal, 0),
      discount: previousMonthData.reduce((sum, item) => sum + item.POrd_Descuento, 0),
      total: previousMonthData.reduce((sum, item) => sum + item.POrd_ValorTotal, 0)
    };

    // Calculate trends
    const calculateGrowthPercentage = (current: number, previous: number): number => {
      if (previous === 0) return current > 0 ? 100 : 0;
      return ((current - previous) / previous) * 100;
    };

    const trends = {
      ordersGrowth: calculateGrowthPercentage(currentMonth.totalOrders, previousMonth.totalOrders),
      subtotalGrowth: calculateGrowthPercentage(currentMonth.subtotal, previousMonth.subtotal),
      discountGrowth: calculateGrowthPercentage(currentMonth.discount, previousMonth.discount),
      totalGrowth: calculateGrowthPercentage(currentMonth.total, previousMonth.total)
    };

    // Get unique services and contracts for filters
    const services = Array.from(new Set(dashboard.Data.map((item: any) => item.Se_Nombre).filter(Boolean)));
    const contracts = Array.from(new Set(dashboard.Data.map((item: any) => item.Contrato).filter(Boolean)));

    return {
      todayIndicator,
      currentMonth,
      previousMonth,
      trends,
      filters: { services, contracts }
    };
  }, [dashboard, selectedServices, selectedContracts]);

  const getTrendIcon = (value: number) => {
    if (value > 0) return '▲';
    if (value < 0) return '▼';
    return '●';
  };

  const getTrendClass = (value: number) => {
    if (value > 0) return 'positive';
    if (value < 0) return 'negative';
    return 'neutral';
  };

  if (!dashboard || !processedData) {
    return (
      <CardAnalytics 
        title='Ventas Personal' 
        contentClass='sales-dashboard' 
        isLoading={dashboard?.Loading} 
        onRefreshClick={handleRefreshClick}
      >
        <div className="no-data">No hay datos disponibles</div>
      </CardAnalytics>
    );
  }

  return (
    <CardAnalytics 
      title='Ventas Personal' 
      contentClass='sales-dashboard' 
      isLoading={dashboard?.Loading} 
      onRefreshClick={handleRefreshClick}
    >
      <div className="sales-dashboard-container">
        {/* Filters Row */}
        <div className="filters-row">
          <TagBox
            dataSource={processedData.filters.services}
            placeholder="Todos los servicios"
            showClearButton={true}
            searchEnabled={true}
            value={selectedServices}
            onValueChanged={(e) => setSelectedServices(e.value || [])}
            multiline={false}
            showSelectionControls={true}
            dropDownOptions={{
              width: 350,
              maxHeight: 300
            }}
            width={180}
            height={32}
          />
          <TagBox
            dataSource={processedData.filters.contracts}
            placeholder="Todos los contratos"
            showClearButton={true}
            searchEnabled={true}
            value={selectedContracts}
            onValueChanged={(e) => setSelectedContracts(e.value || [])}
            multiline={false}
            showSelectionControls={true}
            dropDownOptions={{
              width: 350,
              maxHeight: 300
            }}
            width={180}
            height={32}
          />
        </div>

        {/* Main Content Row */}
        <div className="main-content-row">
          {/* Dashboard Section (70%) */}
          <div className="dashboard-section">
            {/* Period Toggle Buttons */}
            <div className="period-buttons">
              <Button
                text="Este Mes"
                type={selectedPeriod === 'current' ? 'default' : 'normal'}
                stylingMode={selectedPeriod === 'current' ? 'contained' : 'text'}
                onClick={() => setSelectedPeriod('current')}
                className={selectedPeriod === 'current' ? 'active-period' : 'inactive-period'}
              />
              <Button
                text="Mes Pasado"
                type={selectedPeriod === 'previous' ? 'default' : 'normal'}
                stylingMode={selectedPeriod === 'previous' ? 'contained' : 'text'}
                onClick={() => setSelectedPeriod('previous')}
                className={selectedPeriod === 'previous' ? 'active-period' : 'inactive-period'}
              />
            </div>

            {/* Metrics Grid */}
            <div className="metrics-grid">
              {selectedPeriod === 'current' ? (
                <>
                  <div className="metric-card">
                    <div className="metric-value">{formatNumber(processedData.currentMonth.totalOrders)}</div>
                    <div className="metric-label">Órdenes</div>
                    <div className={`trend ${getTrendClass(processedData.trends.ordersGrowth)}`}>
                      {getTrendIcon(processedData.trends.ordersGrowth)} {Math.abs(processedData.trends.ordersGrowth).toFixed(0)}%
                    </div>
                  </div>
                  <div className="metric-card">
                    <div className="metric-value">{formatCurrency(processedData.currentMonth.subtotal)}</div>
                    <div className="metric-label">Subtotal</div>
                    <div className={`trend ${getTrendClass(processedData.trends.subtotalGrowth)}`}>
                      {getTrendIcon(processedData.trends.subtotalGrowth)} {Math.abs(processedData.trends.subtotalGrowth).toFixed(0)}%
                    </div>
                  </div>
                  <div className="metric-card">
                    <div className="metric-value">{formatCurrency(processedData.currentMonth.discount)}</div>
                    <div className="metric-label">Descuentos</div>
                    <div className={`trend ${getTrendClass(processedData.trends.discountGrowth)}`}>
                      {getTrendIcon(processedData.trends.discountGrowth)} {Math.abs(processedData.trends.discountGrowth).toFixed(0)}%
                    </div>
                  </div>
                  <div className="metric-card">
                    <div className="metric-value">{formatCurrency(processedData.currentMonth.total)}</div>
                    <div className="metric-label">Total</div>
                    <div className={`trend ${getTrendClass(processedData.trends.totalGrowth)}`}>
                      {getTrendIcon(processedData.trends.totalGrowth)} {Math.abs(processedData.trends.totalGrowth).toFixed(0)}%
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="metric-card">
                    <div className="metric-value">{formatNumber(processedData.previousMonth.totalOrders)}</div>
                    <div className="metric-label">Órdenes</div>
                  </div>
                  <div className="metric-card">
                    <div className="metric-value">{formatCurrency(processedData.previousMonth.subtotal)}</div>
                    <div className="metric-label">Subtotal</div>
                  </div>
                  <div className="metric-card">
                    <div className="metric-value">{formatCurrency(processedData.previousMonth.discount)}</div>
                    <div className="metric-label">Descuentos</div>
                  </div>
                  <div className="metric-card">
                    <div className="metric-value">{formatCurrency(processedData.previousMonth.total)}</div>
                    <div className="metric-label">Total</div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Today Section (30%) */}
          <div className="today-section">
            <h4 className="today-title">Hoy</h4>
            <div className="today-metrics">
              <div className="today-row">
                <div className="today-metric orders">
                  <div className="today-value">{formatNumber(processedData.todayIndicator.totalOrders)}</div>
                  <div className="today-label">Órdenes</div>
                </div>
              </div>
              <div className="today-row">
                <div className="today-metric subtotal">
                  <div className="today-value">{formatCurrency(processedData.todayIndicator.subtotal)}</div>
                  <div className="today-label">Subtotal</div>
                </div>
                <div className="today-metric discount">
                  <div className="today-value">{formatCurrency(processedData.todayIndicator.discount)}</div>
                  <div className="today-label">Descuento</div>
                </div>
              </div>
              <div className="today-row">
                <div className="today-metric total">
                  <div className="today-value">{formatCurrency(processedData.todayIndicator.total)}</div>
                  <div className="today-label">Total</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </CardAnalytics>
  );
};
